/**
 * Cognito, owned by the web client.
 *
 * This is the one piece of "behaviour" the browser keeps rather than pushing to the
 * server, because it is not app logic — it is a vendor SDK doing a standard auth dance,
 * and proxying it through our own API would only add a hop that can fail.
 *
 * Tokens are held in localStorage. That is forced, not chosen: amazon-cognito-identity-js
 * requires a SYNCHRONOUS storage, and given an async one it silently falls back to memory
 * and signs the user out on every reload. The mitigation is the CSP and the dedicated
 * origin (see infra/lib/saddle-web-stack.ts), not a different store.
 *
 * The ACCESS token is what every Saddle origin wants — the API verifies token_use ===
 * 'access' and rejects an ID token with a 401 even though the same pool signed it.
 */
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  CognitoUserSession,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';

export interface SessionUser {
  id: string;
  email: string;
}

export interface CognitoSettings {
  userPoolId: string;
  clientId: string;
}

/**
 * The SAME storage the shared CognitoService writes through.
 *
 * Not a second localStorage wrapper. `@/storage/mmkv` is aliased to platform/mmkv.web.ts
 * for the browser, and that shim TYPE-TAGS what it stores — a token is written as
 * `s:<jwt>`. A wrapper reading window.localStorage directly would get the tag back as part
 * of the value: a corrupted JWT, a 401 on every call, and a sign-in loop. Two wrappers
 * over one session is how the last sign-out bug happened; this is one implementation with
 * two consumers, until the screens stop using CognitoService entirely.
 */
import { mmkv, storage as kv } from '../platform/mmkv.web';

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

const storage: ICognitoStorage = {
  getItem: (key) => mmkv.getString(key) ?? null,
  setItem: (key, value) => mmkv.setString(key, value),
  removeItem: (key) => {
    mmkv.delete(key);
  },
  /** Only Cognito's own keys — never the rest of the app's data. */
  clear: () => {
    kv.getAllKeys()
      .filter((key) => key.startsWith(COGNITO_PREFIX))
      .forEach((key) => kv.remove(key));
  },
};

let settings: CognitoSettings = { userPoolId: '', clientId: '' };
let pool: CognitoUserPool | null = null;

export function configureAuth(next: CognitoSettings): void {
  settings = next;
  pool = null;
}

/** Null when auth is not configured, rather than throwing on an empty pool id. */
function userPool(): CognitoUserPool | null {
  if (pool) return pool;
  if (!settings.userPoolId || !settings.clientId) return null;
  pool = new CognitoUserPool({
    UserPoolId: settings.userPoolId,
    ClientId: settings.clientId,
    Storage: storage,
  });
  return pool;
}

export function authConfigured(): boolean {
  return Boolean(settings.userPoolId && settings.clientId);
}

/**
 * The pool's UsernameAttributes is email/phone, so the EMAIL is the username. Passing a
 * generated one throws InvalidParameterException — a regression this product has shipped
 * before.
 */
function cognitoUser(identifier: string): CognitoUser {
  const active = userPool();
  if (!active) throw new Error('Auth is not configured.');
  // Same synchronous storage as the pool: with the SDK's async default, sign-in would
  // cache tokens where the pool's getCurrentUser() cannot read them on a cold start.
  return new CognitoUser({ Username: identifier.trim(), Pool: active, Storage: storage });
}

function userFrom(session: CognitoUserSession): SessionUser {
  const payload = session.getIdToken().decodePayload() as { sub?: string; email?: string };
  return { id: payload.sub ?? '', email: payload.email ?? '' };
}

export async function signUp(
  email: string,
  password: string,
  opts: { acceptedTermsAt?: string } = {},
): Promise<void> {
  const active = userPool();
  if (!active) throw new Error('Auth is not configured.');
  const attributes = [
    new CognitoUserAttribute({ Name: 'email', Value: email.trim() }),
    new CognitoUserAttribute({
      Name: 'custom:terms_accepted_at',
      Value: opts.acceptedTermsAt ?? new Date().toISOString(),
    }),
  ];
  return new Promise((resolve, reject) => {
    active.signUp(email.trim(), password, attributes, [], (err) =>
      err ? reject(new Error(err.message)) : resolve(),
    );
  });
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmRegistration(code.trim(), true, (err) =>
      err ? reject(new Error(err.message)) : resolve(),
    );
  });
}

export async function resendConfirmation(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).resendConfirmationCode((err) => (err ? reject(new Error(err.message)) : resolve()));
  });
}

export async function signIn(email: string, password: string): Promise<SessionUser> {
  const user = cognitoUser(email);
  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: email.trim(), Password: password }), {
      onSuccess: (session) => resolve(userFrom(session)),
      onFailure: (err: Error) => reject(new Error(err.message)),
      newPasswordRequired: () => reject(new Error('This account needs a new password. Please contact support.')),
    });
  });
}

export async function signOut(): Promise<void> {
  userPool()?.getCurrentUser()?.signOut();
  storage.clear();
}

export async function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(new Error(err.message)),
      inputVerificationCode: () => resolve(),
    });
  });
}

export async function confirmForgotPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    cognitoUser(email).confirmPassword(code.trim(), newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(new Error(err.message)),
    });
  });
}

/** Resolves the stored session, refreshing it if the SDK decides it must. */
export async function currentUser(): Promise<SessionUser | null> {
  const user = userPool()?.getCurrentUser();
  if (!user) return null;
  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        storage.clear();
        resolve(null);
        return;
      }
      resolve(userFrom(session));
    });
  });
}

/**
 * The access token, read synchronously.
 *
 * Synchronous because every API call needs it and the SDK's session callback fires
 * immediately when the session is already cached. Returns null rather than a stale token
 * once it has expired, so a caller gets a clean 401 instead of a confusing one.
 */
export function accessToken(): string | null {
  const user = userPool()?.getCurrentUser();
  if (!user) return null;
  let token: string | null = null;
  user.getSession((err: Error | null, session: CognitoUserSession | null) => {
    if (!err && session?.isValid()) token = session.getAccessToken().getJwtToken();
  });
  return token;
}

export async function deleteAccount(): Promise<void> {
  const user = userPool()?.getCurrentUser();
  if (!user) throw new Error('You are not signed in.');
  await new Promise<void>((resolve, reject) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) {
        reject(new Error('Your session has expired. Please sign in again.'));
        return;
      }
      user.deleteUser((delErr) => {
        if (delErr) {
          reject(new Error(delErr.message));
          return;
        }
        storage.clear();
        resolve();
      });
    });
  });
}
